import { UsersRequestResponseParser } from '@img-arena/consoles-api-streaming'
import { PostAwsProvisionParser } from '@/types/streams/awsProvision'
import { GetEventParser } from '@/types/streams/event'
import { GetEventEncoderDetailsParser } from '@/types/streams/eventEncoder'
import { GetEventPipelineStatusParser } from '@/types/streams/eventPipelineStatus'
import {
  GeoBlockingTemplateResponseParser,
  GeoBlockingTemplatesResponseParser
} from '@/types/streams/geoBlocking'
import { PatchLicenseResponseParser } from '@/types/streams/license'
import { OperatorParser } from '@/types/streams/operator'
import { GetStreamParser } from '@/types/streams/stream'

const DGE_API_V1_BASE_URL = `${import.meta.env.VITE_ARGONATH_API}/api/v1`
const DGE_API_V3_BASE_URL = `${import.meta.env.VITE_ARGONATH_API}/api/v3`
const DGE_API_BASE_URL = `${import.meta.env.VITE_ARGONATH_API}/api`

export const DGE_ENDPOINTS = {
  Auth: {
    url: `${DGE_API_V1_BASE_URL}/authenticate`,
    CurrentUser: {
      url: `${DGE_API_V1_BASE_URL}/current_user`
    }
  },
  ForgotPassword: (email: string) => ({
    url: `${DGE_API_V1_BASE_URL}/account/remind-password?email=${encodeURIComponent(email)}`
  }),
  Operators: {
    get: {
      url: `${DGE_API_V1_BASE_URL}/operators`
    },
    put: {
      url: `${DGE_API_V1_BASE_URL}/operators`,
      parser: OperatorParser
    },
    Operator: {
      Licenses: {
        get: (operatorId: string) => ({
          url: `${DGE_API_V1_BASE_URL}/operators/${encodeURIComponent(operatorId)}/licenses` as const
        }),
        post: {
          url: `${DGE_API_V1_BASE_URL}/licenses` as const
        },
        put: (licenseProperty: string) => ({
          url: `${DGE_API_V1_BASE_URL}/licenses/${licenseProperty}` as const,
          parser: PatchLicenseResponseParser
        }),
        LicenseProperty: {
          Suspend: {
            delete: (licenseId: string) => ({
              url: `${DGE_API_V1_BASE_URL}/licenses/${licenseId}` as const
            })
          },
          Unsuspend: {
            post: (licenseId: string) => ({
              url: `${DGE_API_V1_BASE_URL}/licenses/${licenseId}/enable` as const
            })
          }
        }
      }
    }
  },
  Users: {
    get: {
      url: `${DGE_API_V1_BASE_URL}/accounts`,
      parser: UsersRequestResponseParser
    },
    put: {
      url: `${DGE_API_V1_BASE_URL}/accounts`
    },
    User: {
      delete: (userId: string) => ({
        url: `${DGE_API_V1_BASE_URL}/accounts/${encodeURIComponent(userId)}`
      }),
      edit: (userId: string) => ({
        url: `${DGE_API_V1_BASE_URL}/accounts/${encodeURIComponent(userId)}`
      })
    }
  },
  Event: {
    get: (eventId: string) => ({
      url: `${DGE_API_V1_BASE_URL}/events/${eventId}`,
      parser: GetEventParser
    }),
    EncodeDetails: {
      get: (eventId: string) => ({
        url: `${DGE_API_V1_BASE_URL}/events/${eventId}/encode-details`,
        parser: GetEventEncoderDetailsParser
      })
    }
  },
  Licenses: {
    License: {
      get: (licenseId: string) => ({
        url: `${DGE_API_V1_BASE_URL}/licenses/${licenseId}` as const
      })
    },
    Properties: {
      get: {
        url: `${DGE_API_V1_BASE_URL}/sports`
      }
    }
  },
  ImportEvents: {
    Tournaments: {
      get: () => ({
        url: `${DGE_API_V1_BASE_URL}/external/tournaments`
      })
    },
    Tournament: {
      get: (tournamentId: string) => ({
        url: `${DGE_API_V1_BASE_URL}/external/tournaments/${tournamentId}`
      })
    }
  },
  GeoTemplates: {
    get: {
      url: `${DGE_API_V1_BASE_URL}/geo-templates`,
      parser: GeoBlockingTemplatesResponseParser
    },
    post: {
      url: `${DGE_API_V1_BASE_URL}/geo-templates`,
      parser: GeoBlockingTemplateResponseParser
    },
    put: (templateId: string) => ({
      url: `${DGE_API_V1_BASE_URL}/geo-templates/${templateId}`,
      parser: GeoBlockingTemplateResponseParser
    }),
    delete: (templateId: string) => ({
      url: `${DGE_API_V1_BASE_URL}/geo-templates/${templateId}`
    })
  },
  Streaming: {
    get: (eventId: string) => ({
      url: `${DGE_API_V3_BASE_URL}/streaming/events/${eventId}/stream`,
      parser: GetStreamParser
    }),
    Event: {
      PipelineStatus: {
        get: (eventId: string, streamingConfigId: string) => ({
          url: `${DGE_API_BASE_URL}/poirot/pipelines/${eventId}-${streamingConfigId}`,
          parser: GetEventPipelineStatusParser
        })
      },
      Provision: {
        delete: (eventId: string, awsRegionCode: string) => ({
          url: `${DGE_API_V1_BASE_URL}/events/${eventId}/${awsRegionCode}/pipelines`
        }),
        post: (eventId: string) => ({
          url: `${DGE_API_V1_BASE_URL}/events/${eventId}/pipelines`,
          parser: PostAwsProvisionParser
        })
      }
    }
  }
} as const
